import { KeyValue } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  forwardRef,
  input,
  inject,
  Injector,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { InputLabelOptions, TypographyKeys } from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';
import { renderOptionsWithHandlebars } from '../input-options/input-options.utils';
import { GenericHandleBarModel } from '../services/handlebar-model.service';

@Component({
  standalone: false,
  selector: 'finxone-autocomplete-input',
  templateUrl: './finxone-autocomplete-input.component.html',
  styleUrls: ['./finxone-autocomplete-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneAutocompleteInputComponent),
      multi: true,
    },
  ],
})
export class FinxoneAutocompleteInputComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);
  private injector = inject(Injector);

  @ViewChild('inputElementWrapper') inputElementWrapper: ElementRef;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @ViewChild('autocompleteWrapper') autocompleteWrapperRef!: ElementRef<HTMLElement>;

  // Core inputs
  @Input() public label = '';
  @Input() public placeholder = '';
  @Input() public class = '';
  @Input() public readOnly = false;
  @Input() public isDisabled = false;
  @Input() public formControlInvalid = false;
  @Input() public globalCssVariables: Record<string, string> = {};
  @Input() public customErrorTemplate?: TemplateRef<unknown>;
  @Input() public id = '';
  @Input() public helperText = '';
  @Input() public inputLabelTooltipText = '';
  @Input() inputIcon: string;
  @Input() toggleIcon: string;
  @Input() loading = false;
  // In-build classes for selected dropdown item
  @Input() itemClass =
    '!bg-inherit !pt-[10px] !pb-[10px] !pl-[10px] !pr-0 data-[selected]:!bg-[var(--color-active)] data-[selected]:!text-[var(--color-surface)]';
  //Dropdown list item styles
  @Input() public dropdownItemListStyles: Record<string, string> = {
    background: 'var(--inputFieldIdleBackgroundColor)',
    'border-color': 'var(--inputFieldIdleBorderColor)',
    'border-top-width': 'var(--inputFieldBorderTopWidth)',
    'border-bottom-width': 'var(--inputFieldBorderBottomWidth)',
    'border-left-width': 'var(--inputFieldBorderLeftWidth)',
    'border-right-width': 'var(--inputFieldBorderRightWidth)',
  };

  // Autocomplete-specific inputs
  @Input() public filteredOptions: any[] = [];
  @Input() public selectedOption: any;
  @Input() public optionLabelKey = 'label';
  @Input() public optionValueKey = 'value';
  @Input() public customOptionTemplate?: TemplateRef<unknown>;
  @Input() formGroup!: FormGroup;
  @Input() formControlName: string;

  // Validation Configuration
  @Input() validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;
  @Output() public valueChanged = new EventEmitter<any>();
  @Output() public searchChanged = new EventEmitter<string>();
  @Output() public blurEvent = new EventEmitter();
  @Output() public focusEvent = new EventEmitter();
  // Parent form control
  @Input() parentControl?: FormControl;
  // Internal form control
  public control = new FormControl();

  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public isInputOnFocus = false;
  private cssVars: Record<string, string> = {};
  public spaceBetweenElementUnit = input('');
  public spaceBetweenElementValue = input(0);
  protected readonly spaceBetweenItem = computed(() => {
    return `${this.spaceBetweenElementValue()}${this.spaceBetweenElementUnit()}`;
  });
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';
  private optionList: any[] = [];
  private renderedOptionList: any[] = [];
  public transformFn = (option: any) => option?.[this.optionLabelKey] ?? '';
  public textStylingClass: TypographyKeys;
  public borderRadiusClass: string;
  public handlebarModel: GenericHandleBarModel;

  constructor() {
    super();
    effect(() => {
      this.applyGlobalInputStyles();
    });
  }

  ngOnInit(): void {
    if (this.parentControl) this.control = this.parentControl;
    this.optionList = [...this.filteredOptions];
    this.renderAllOptionLabels();
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filteredOptions']?.currentValue !== undefined) {
      this.optionList = [...changes['filteredOptions'].currentValue];
    }
    if (changes['selectedOption']?.currentValue !== undefined) {
      const option = changes['selectedOption'].currentValue;
      const primitiveValue =
        option != null && typeof option === 'object' ? option[this.optionValueKey] : option;
      this.control.setValue(primitiveValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (!this.parentControl) {
      if (changes['formControlInvalid']?.currentValue !== undefined) {
        this.setFormControlValidity(this.formControlInvalid);
      }
    }
  }

  private applyGlobalInputStyles(): void {
    const globalCssVariables = this.configService.globalCssVariables();

    if (!globalCssVariables) {
      return;
    }

    this.cssVars = globalCssVariables;
    this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
    this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
    this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
    this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
    this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
  }

  private setFormControlValidity(formControlInvalid: boolean): void {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  // ControlValueAccessor
  public writeValue(value: any): void {
    const primitiveValue = value != null && typeof value === 'object' ? value[this.optionValueKey] : value;
    const match = this.renderedOptionList.find((opt) => opt[this.optionValueKey] === primitiveValue);
    this.selectedOption =
      match ?? (primitiveValue != null ? { [this.optionValueKey]: primitiveValue } : undefined);
    this.cd.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }

  // Autocomplete event handlers
  public onValueChange(value: any | null): void {
    if (value == null || typeof value !== 'object') {
      return;
    }
    this.selectedOption = value ?? undefined;
    const selectedItem = value != null ? (value[this.optionValueKey] ?? value) : null;
    this.control.setValue(selectedItem);
    this.control.markAsDirty();
    this.onChange(selectedItem);
    this.valueChanged.emit(selectedItem);
    this.cd.markForCheck();
  }

  public onSearchChange(search: string): void {
    if (search === '') {
      this.selectedOption = '';
      this.control.setValue('');
      this.filteredOptions = [...this.renderedOptionList];
      this.searchChanged.emit('');
      this.cd.markForCheck();
      return;
    }

    this.filteredOptions = this.renderedOptionList.filter((opt) =>
      opt[this.optionLabelKey]?.toLowerCase().includes(search.toLowerCase()),
    );
    this.searchChanged.emit(search);
    this.cd.markForCheck();
  }

  public onFocus(): void {
    this.isInputOnFocus = true;
    this.focusEvent.emit();
  }

  public onBlur(): void {
    this.isInputOnFocus = false;
    this.onTouched();
    this.blurEvent.emit();
  }

  public handleLabelClick(): void {
    if (this.labelType === this.labelTypeOptions.FLOATING) {
      this.inputElementWrapper?.nativeElement.querySelector('input')?.focus();
    }
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }

  private renderAllOptionLabels(): void {
    renderOptionsWithHandlebars(this.optionList, this.optionLabelKey, this.optionValueKey, this.injector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.handleRenderedOptions(data));
  }

  private handleRenderedOptions(data: {
    renderedOptions: Record<string, string>;
    optionsWithRenderedLabelAndKey: Record<string, string>[];
    model: GenericHandleBarModel;
  }): void {
    this.handlebarModel = data.model;
    // Store fully rendered options
    this.renderedOptionList = data?.optionsWithRenderedLabelAndKey ?? [];
    // Update filtered options initially with full list
    this.filteredOptions = [...this.renderedOptionList];

    const primitiveValue =
      this.selectedOption != null && typeof this.selectedOption === 'object'
        ? this.selectedOption[this.optionValueKey]
        : this.selectedOption;

    const currentValue = primitiveValue ?? this.control.value;
    if (currentValue === null) return;

    const matchedOption = this.renderedOptionList.find((opt) => opt[this.optionValueKey] === currentValue);
    if (!matchedOption) return;

    this.selectedOption = matchedOption;

    this.cd.markForCheck();
  }
}
